import moment from 'moment-timezone';
import { endsWith } from 'lodash';
import { SettingsService } from '@manager-base/settings/settings';
import { Locale } from '@buildium/angularjs-core/dist/definitions/localization/services/locale';
import { IBdLocalize } from '@buildium/angularjs-core/dist/definitions/components/resource-strings/localize-provider';

export class DateTimeLocalizationUtilities {
    public static $inject: string[] = [
        'Settings',
        'Locale',
        'BdLocalize',
    ];

    constructor(
        private Settings: SettingsService,
        private locale: Locale,
        private BdLocalize: IBdLocalize,
    ) {
    }

    // This will take today's date and time in UTC and convert it to the customer's time zone
    getNowMomentInCustomerTimeZone(): moment.Moment {
        return moment().tz(this.Settings.SubscriberSettingsInfo.TzInfoName);
    }

    getCustomerTimeZoneDescription(): string {
        return this.Settings.SubscriberSettingsInfo.TimeZoneDescription.replace(/^\(.*?\)\s*/, '');
    }

    isCustomerTimeTwelveHourTimeFormat(): boolean {
        const nowMoment = this.getNowMomentInCustomerTimeZone();
        const nowFormatted = nowMoment.format();
        const nowFormattedDateTime = this.locale.formatDateTime(nowFormatted, true);
        return endsWith(nowFormattedDateTime, 'M');
    }

    getUtcDateTimeStringFromInputFields(inputDate: string, inputHours: string, inputMinutes: string, inputMeridiem: string): string {
        // Due to a quirk with moment.tz that can shift the date of a time when changing the time zone,
        // we'll parse the input date up front in one moment, and then apply that to another moment
        // after we manually set the time zone.
        let parsedDateMoment = moment(inputDate, this.locale.getDateFormat().toUpperCase());
        if (!parsedDateMoment.isValid()) {
            parsedDateMoment = moment(inputDate);
        }

        let hoursAdjusted = parseInt(inputHours, 10);
        if (inputMeridiem === 'PM' && hoursAdjusted < 12) {
            hoursAdjusted += 12;
        } else if (inputMeridiem === 'AM' && hoursAdjusted === 12) {
            hoursAdjusted -= 12;
        }

        return moment()
            .tz(this.Settings.SubscriberSettingsInfo.TzInfoName)
            .set({
                year: parsedDateMoment.year(),
                month: parsedDateMoment.month(),
                date: parsedDateMoment.date(),
                hours: hoursAdjusted,
                minutes: parseInt(inputMinutes, 10),
                seconds: 0,
            })
            .utc()
            .format();
    }

    formatDateInCustomerTimeZone(dateTime: string): string {
        return this.locale.formatDate(dateTime, undefined);
    }

    formatInCustomerTimeZone(dateTime: string): string {
        return this.locale.formatDateTime(dateTime, true);
    }

    formatAgeInCustomerTimeZone(dateTime: string): string {
        const nowMoment = moment().tz(this.Settings.SubscriberSettingsInfo.TzInfoName);
        const dateTimeMoment = moment(dateTime).tz(this.Settings.SubscriberSettingsInfo.TzInfoName);
        const duration = moment.duration(nowMoment.diff(dateTimeMoment));
        const totalHours = duration.asHours();
        const days = Math.floor(totalHours / 24);
        const hours = Math.floor(totalHours % 24);
        const hoursString = hours === 1
            ? this.BdLocalize.getString('Global.Age.Hours.Singular', '1 hour')
            : this.BdLocalize.getString('Global.Age.Hours.Plural', `${hours} hours`, [hours.toString()]);
        if (days <= 0) {
            return hours > 0
                ? hoursString
                : this.BdLocalize.getString('Global.Age.Minutes', '< 1 hour');
        }

        const daysString = days === 1
            ? this.BdLocalize.getString('Global.Age.Days.Singular', '1 day')
            : this.BdLocalize.getString('Global.Age.Days.Plural', `${days} days`, [days.toString()]);

        return hours > 0
            ? this.BdLocalize.getString('Global.Age.HoursAndDays', `${daysString} ${hoursString}`, [daysString, hoursString])
            : daysString;
    }
}
